import { useTranslations } from "next-intl";

import { NodeTextField } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { FormToggle } from "@/components/form/FormToggle";
import { Sidebar } from "@/components/sidebar";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { LabeledInput } from "@/components/sidebar/LabeledInput";
import { NumberInput } from "@/components/sidebar/NumberInput";

import { THREE_D_CHART_TYPES } from "./constants";

import type { AnalysisType, ChartOptions } from "./dataAnalysisUtils";

export function ChartCustomization({
  options,
  onChange,
  activeType,
}: {
  options: ChartOptions;
  onChange: (patch: Partial<ChartOptions>) => void;
  activeType: AnalysisType;
}) {
  const t = useTranslations("sidebar.activeNode.dataAnalysis.customize");

  const showPolyDegree = activeType === "polyFit";
  const showHistogramBins = activeType === "histogram";
  const showGrid = !THREE_D_CHART_TYPES.has(activeType);

  return (
    <ActionsDisclosure
      isNested
      title={t("title")}
      className="w-full mt-1"
      disclosureProps={{ defaultOpen: false }}
    >
      <div className="flex flex-col gap-2">
        <LabeledInput
          label={t("chartTitle")}
          value={options.title}
          onChange={(val) => onChange({ title: val })}
        />

        {/* Dual-column like GraphAxisLabels */}
        <Sidebar.SectionTitle className="text-xxxs text-gray-500 pt-0">
          {t("labels")}
        </Sidebar.SectionTitle>
        <div className="flex bg-document-bg shadow-block dark:shadow-block-inverted rounded-6">
          <NodeTextField
            id="chart-x-axis"
            value={options.xAxisLabel}
            className="border-r border-r-gray-200 dark:border-r-gray-700"
            onChange={(e) => onChange({ xAxisLabel: e.target.value })}
            labelProps={{ label: "x" }}
          />
          <NodeTextField
            id="chart-y-axis"
            value={options.yAxisLabel}
            onChange={(e) => onChange({ yAxisLabel: e.target.value })}
            labelProps={{ label: "y" }}
          />
        </div>

        {/* Hidden for 3D charts whose grid lives in scene axes. */}
        {showGrid && (
          <label
            htmlFor="chart-show-grid"
            className="flex items-center cursor-pointer text-xxs tracking-tight p-1.25 pb-0 uppercase"
          >
            {t("showGrid")}
            <FormToggle
              id="chart-show-grid"
              checked={options.showGrid}
              className="ml-auto"
              onClick={(e) => e.stopPropagation()}
              onChange={() => onChange({ showGrid: !options.showGrid })}
              asInput
            />
          </label>
        )}

        {(showPolyDegree || showHistogramBins) && (
          <>
            <Sidebar.Delimiter className="mt-0.5 mb-0.5 mx-[1px]" />
            <Sidebar.SectionTitle className="text-xxxs text-gray-500 pt-0">
              {t("options")}
            </Sidebar.SectionTitle>

            {showPolyDegree && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xxxs font-mono uppercase tracking-tight text-gray-500">
                  {t("polyDegree")}
                </span>
                <NumberInput
                  value={options.polyDegree}
                  min={1}
                  max={10}
                  aria-label={t("polyDegree")}
                  onSubmit={(val) => onChange({ polyDegree: val })}
                />
              </div>
            )}

            {showHistogramBins && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xxxs font-mono uppercase tracking-tight text-gray-500">
                  {t("histogramBins")}
                </span>
                <NumberInput
                  value={options.histogramBins || ""}
                  placeholder={t("auto")}
                  min={0}
                  max={100}
                  aria-label={t("histogramBins")}
                  onSubmit={(val) => onChange({ histogramBins: val })}
                />
              </div>
            )}
          </>
        )}
      </div>
    </ActionsDisclosure>
  );
}
